import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, Plus, Loader2 } from 'lucide-react';
import { Sidebar } from '../../components/Sidebar';
import { useAuth } from '../../context/AuthContext';
import { SavedBriefsTable } from './briefs/SavedBriefsTable';
import { BriefDetailSheet } from './briefs/BriefDetailSheet';
import { api as briefApi, AE_ROLES, API_URL, errMsg } from './briefs/briefApi';

export default function SavedBriefsPage() {
  const { user } = useAuth();
  const isAE = AE_ROLES.includes(user?.role);
  const nav = useNavigate();
  const [aeBriefs, setAeBriefs] = useState(null);
  const [saved, setSaved] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    if (isAE) briefApi.get('').then((d) => setAeBriefs(d.briefs)).catch((e) => { setAeBriefs([]); toast.error(errMsg(e, 'Could not load briefs')); });
    else setAeBriefs([]);
    axios.get(`${API_URL}/api/ideation/briefs`, { withCredentials: true }).then(({ data }) => setSaved(data.briefs)).catch(() => setSaved([]));
  }, [isAE]);
  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try { await briefApi.patch(`/${id}/status`, { status }); toast.success(`Brief ${status}`); load(); } catch (e) { toast.error(errMsg(e, 'Could not update status')); }
  };
  const ready = aeBriefs !== null && saved !== null;
  const all = ready ? [...aeBriefs, ...saved.map((b) => ({ ...b, kind: 'saved' }))].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))) : [];

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D] min-w-0" data-testid="saved-briefs-page">
        <div className="max-w-[1400px] mx-auto space-y-6">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3"><ClipboardList className="text-[#FF451A]" size={30} /> Saved Briefs</h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Every brief you've created or saved from Brief Suggestions.</p>
            </div>
            <button onClick={() => nav('/ideation')} className="rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 bg-[#FF451A] hover:bg-[#E63E17] text-white transition-colors" data-testid="saved-briefs-new-btn"><Plus size={13} /> New from Brief Suggestions</button>
          </div>
          {!ready ? <Loader2 className="animate-spin text-[#FF451A]" size={18} /> : (
            <SavedBriefsTable briefs={all} onLoad={(b) => nav(isAE ? `/ideation?brief=${b.id}` : `/ideation?saved=${b.id}`)} onOpen={setDetailId}
              onOpenSaved={isAE ? setDetailId : (id) => nav(`/ideation?saved=${id}`)} onStatus={setStatus} />
          )}
        </div>
      </div>
      {isAE && <BriefDetailSheet briefId={detailId} onClose={() => setDetailId(null)} onChanged={load} onLoad={(b) => nav(`/ideation?brief=${b.id}`)} />}
    </div>
  );
}
